import Vue from 'vue';
import Vuex from 'vuex';
import appSettings from '../assets/appSettings_parser_v4.json';
import {
  updateUserSettingsApi,
  getUserSettingsApi,
} from '../services/user.service';
import { getUserDatabases } from '../services/userTable';
import { getFlowTaskDataObject } from '../utils/helpers';
import { Auth } from 'aws-amplify';
import { getWorkFlowTags, getFlowsList } from '../services/axios/data.api';
import { getWorkflowDatabaseTaskSchema } from '../utils/helpers/WorkFlowHelpers';

Vue.use(Vuex);

// eslint-disable-next-line no-unused-vars
const standardSettings = {
  isLimitLoadEditToDateInterval: false,
  isLimitLoadEditToTimeInterval: false,
  isLimitLoadEditToDayInterval: false,
};

const initialState = {
  user: null,
  appSettings: appSettings,
  userSettings: null,
  addNewFlowPermission: false,
  flows: [],
  databaseItems: {},
  isLoading: false,
  isAdmin: false,
  isDatabaseDragging: false,
  databaseList: [],
  workflowsPerTag: [],
  tagsPerWorkflow: {},
  isFlowsLoading: false,
  paginationDbs: [],
  isNavbarResizing: false,
  sidebarLinkedCustomDBSettings: null,
};

const store = new Vuex.Store({
  // eslint-disable-next-line no-undef
  state: structuredClone(initialState),
  getters: {
    getAccessTokenExpiry(state) {
      return state.user && state.user.signInUserSession
        ? state.user.signInUserSession.accessToken.payload.exp
        : null;
    },
    getAllFlows(state) {
      return state.flows;
    },
    getJWTToken(state) {
      return state.user
        ? state.user.signInUserSession.accessToken.jwtToken
        : null;
    },
    getUser(state) {
      return state.user;
    },
    getUserName(state) {
      return state.user ? state.user.attributes.email.split('@')[0] : '';
    },
    getUserEmail(state) {
      return state.user ? state.user.attributes.email : '';
    },
    getIsAdmin(state) {
      return state.isAdmin;
    },
    getUserSetting(state) {
      return state.userSettings;
    },
    getDatabaseDragging(state) {
      return state.isDatabaseDragging;
    },
    getUserId(state) {
      return state.user ? state.user.username : '';
    },
    getDatabaseList(state) {
      return state.databaseList;
    },
    getUserPermissionToAddFlow(state) {
      return state.addNewFlowPermission;
    },
    getWorkflowsPerTag(state) {
      return state.workflowsPerTag;
    },
    getTagsPerWorkflow(state) {
      return state.tagsPerWorkflow;
    },
    getFlowsLoading(state) {
      return state.isFlowsLoading;
    },
    getPaginationDBs(state) {
      return state.paginationDbs;
    },
    isNavbarResizing(state) {
      return state.isNavbarResizing;
    },
    getsidebarLinkedCustomDBSettings(state) {
      return state.sidebarLinkedCustomDBSettings;
    },
  },
  mutations: {
    clearStore() {
      store.replaceState(JSON.parse(JSON.stringify(initialState)));
    },
    setUser(state, user) {
      state.user = user;
    },
    setIsAdmin(state, isAdmin) {
      state.isAdmin = isAdmin;
    },
    setUserSettings(state, userSettings) {
      state.appSettings.settings = userSettings;
    },
    setUserSettingsDB(state, userSettings) {
      state.userSettings = userSettings;
    },
    addFlow(state, flows) {
      state.flows = flows;
    },
    addFlowPermission(state, addNewFlowPermission) {
      state.addNewFlowPermission = addNewFlowPermission;
    },
    databaseItems(state, database) {
      state.databaseItems[database.name] = database.items;
    },
    setLoading(state, loading) {
      state.isLoading = loading;
    },
    CHANGE_SINGLE_FLOW_NAME(state, flowPayload) {
      state.flows[flowPayload.flowIndex].name = flowPayload.newName;
    },
    SET_USER(state, flowPayload) {
      state.user = flowPayload.user;
    },
    REMOVE_SINGLE_FLOW(state, flowId) {
      state.flows = state.flows.filter((flow) => flow.id != flowId);
    },
    SET_DATABASE_DRAGGING(state, isDrag) {
      state.isDatabaseDragging = isDrag;
    },
    SET_DATABASE_LIST(state, databaseArray) {
      state.databaseList = databaseArray;
    },
    SET_DATABASE_SCHEMA(state, data) {
      const database = state.databaseList.find((db) => db.name == data.id);
      if (database) {
        Vue.set(database, 'schema', data.data);
        Vue.set(database, 'layout', data.layout);
      }
    },
    SET_WORKFLOWS_PER_TAG(state, workflowsPerTag) {
      state.workflowsPerTag = workflowsPerTag;
    },
    SET_TAGS_PER_WORKFLOW(state, tagsPerWorkflow) {
      state.tagsPerWorkflow = tagsPerWorkflow;
    },
    ADD_PAGINATION_DBS(state, databases) {
      state.paginationDbs = databases;
    },
    toggleNavbarResizing(state, toggle) {
      state.isNavbarResizing = toggle;
    },
    setSidebarLinkedCustomDBSettings(state, setting) {
      state.sidebarLinkedCustomDBSettings = setting;
    },
  },
  actions: {
    async updateUserSettings({ commit }, updatedSettings) {
      const response = await updateUserSettingsApi(this.state.user.username, {
        settings: updatedSettings,
      });

      if (
        response.data &&
        response.status === 200 &&
        response.data.HTTPStatusCode === 200
      ) {
        commit('setUserSettingsDB', updatedSettings);
      }
    },
    getUserSettings({ commit }) {
      if (this.state.user) {
        let userSettings = localStorage.getItem(
          this.state.user.username + '.userSettings'
        );
        if (userSettings) commit('setUserSettings', JSON.parse(userSettings));
      }
    },
    async getUserSettingsDB({ commit }) {
      if (this.state.user) {
        try {
          const settings = await getUserSettingsApi(
            this.state.user.username,
            true
          );

          if (settings && settings.status === 200 && settings.data) {
            commit('setUserSettingsDB', settings.data);
          }
        } catch (error) {
          commit('setUserSettingsDB', null);
        }
      }
    },
    // eslint-disable-next-line no-unused-vars
    async putUserSettings({ commit, state, dispatch }, params) {
      var url = process.env.VUE_APP_DATA_API;
      url += '/users/';
      url += this.state.user.username;
      url += '/settings';

      let myHeaders = new Headers();
      myHeaders.append(
        'Authorization',
        'Bearer ' + this.state.user.signInUserSession.accessToken.jwtToken
      );
      myHeaders.append('Content-Type', 'application/json');

      let payload = {
        settings: params.settings,
      };

      return await fetch(url, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: myHeaders,
      })
        .then(async (data) => {
          return data.json();
        })
        .then(async (json) => {
          await dispatch('getUserSettingsDB');
          return json;
        });
    },
    async getFlows({ commit }) {
      try {
        this.state.isFlowsLoading = true;
        const flows = await getFlowsList();
        if (flows?.data?.length) {
          flows.data.forEach((flow) => {
            if (!flow.hasOwnProperty('tags')) {
              flow['tags'] = [];
            }
          });
        }
        commit('addFlow', flows.data);
      } catch (error) {
        // console.log('Error while getting flows', error);
      } finally {
        this.state.isLoading = false;
        this.state.isFlowsLoading = false;
      }
    },
    // eslint-disable-next-line no-unused-vars
    async createNewFlow({ commit, state, dispatch }, params) {
      var dataType, tasks;

      if (params.type == 'app') {
        dataType = '';

        if (params.navbarElements) {
          const taskSchema = getFlowTaskDataObject();
          const databaseTaskSchema = getWorkflowDatabaseTaskSchema(
            taskSchema,
            params
          );
          taskSchema.props.formTrigger.elements = [...params['navbarElements']];
          tasks = [taskSchema, databaseTaskSchema];
        } else {
          tasks = [];
        }
      } else {
        // Set default values for 'timeseries' action
        dataType = params.dataType;
        var fileKey = params.fileKey;
        var itemLabel;
        if (dataType == 'loadprofile') itemLabel = 'timeseries';
        else itemLabel = 'custom';
        tasks = [{ ...getFlowTaskDataObject({ fileKey, itemLabel }) }];
      }

      const workFlowCaptionDate = new Date().toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });

      const workFlowCaptionTime = new Date().toLocaleString('de-De', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

      let flow = {
        id: params.id,
        owner: state.user.username,
        tableID: params.tableID,
        type: params.type,
        dataType: dataType,
        lastExecution: '',
        lastExecutionStatus: true,
        name: 'Flow ' + workFlowCaptionDate + ' ' + workFlowCaptionTime,
        tasks: tasks,
        settings: {
          btnCaption: 'Starten',
          apiEndpoint: 'https://flow-runner.api.codelos.io/v6',
          loadingCaption: 'Ladevorgang läuft...',
          fullWidth: false,
          showResultInForm: false,
          brandingColor: '#000',
          domains: ['*.codelos.io'],
          reference: {
            storageOption: '',
            label: '',
            type: '',
            value: '',
          },
          authentication: {
            authUrl: '',
            location: 'header',
            storageOption: '',
            label: '',
            redirectUrl: '',
          },
          formBuilder: 'v6',
        },
        tags: [],
      };

      let rslt = await dispatch('saveFlow', {
        flow: flow,
      });

      return rslt;
    },
    // eslint-disable-next-line no-unused-vars
    async saveFlow({ commit, state }, params) {
      //copy of the flow without output and input
      var data = Object.assign({}, JSON.parse(JSON.stringify(params.flow)));
      data.tasks.forEach((part, index, arr) => {
        arr[index].output = [];
        arr[index].input = [];
      });

      //set API params
      let formData = new FormData();
      var url = process.env.VUE_APP_DATA_API;

      let myHeaders = new Headers();
      myHeaders.append(
        'Authorization',
        'Bearer ' + this.state.user.signInUserSession.accessToken.jwtToken
      );

      url += '/flow';
      formData.append('flow', JSON.stringify(data));

      //call api
      return await fetch(url, {
        body: formData,
        headers: myHeaders,
        method: 'put',
      })
        .then(async (response) => {
          if (response.status == 200) return response.json();
          else if (response.status == 401)
            throw new Error('Access Token has expired');
          else {
            let jsn = await response.text().then((jsn) => {
              return JSON.parse(jsn)['message'];
            });
            throw jsn;
          }
        })
        .then(async () => {
          return Number(data.id);
        })
        .catch((e) => {
          if (e == 'Error: Access Token has expired') {
            return 'Access Token has expired';
          } else {
            return 'Error: ' + e;
          }
        });
    },
    fetchFlowAccessLevel({ commit, state }) {
      var url = process.env.VUE_APP_DATA_API;
      url += '/users/' + state.user.username;
      url += '/policies?resource=flows';

      let myHeaders = new Headers();
      myHeaders.append(
        'Authorization',
        'Bearer ' + state.user.signInUserSession.accessToken.jwtToken
      );

      return fetch(url, {
        headers: myHeaders,
        method: 'GET',
      })
        .then((response) => {
          return response.json();
        })
        .then((json) => {
          if (
            json.policies.length > 0 &&
            json.policies[0].access_level == 'edit'
          ) {
            commit('addFlowPermission', true);
          } else {
            commit('addFlowPermission', false);
          }
        });
    },
    changeSingleFlowNameAction({ commit }, flowPayload) {
      commit('CHANGE_SINGLE_FLOW_NAME', flowPayload);
    },
    setUserDetails({ commit }, flowPayload) {
      commit('SET_USER', flowPayload);
    },
    removeSingleFlowByIdAction({ commit }, flowId) {
      commit('REMOVE_SINGLE_FLOW', flowId);
    },
    async filterSettingBasedOnDatabases({ state, dispatch }, payload) {
      const stateSettings = state.userSettings;
      let updatedMenuBarSortArray = [];
      let isUpdatedSettings = false;
      if (payload && stateSettings?.menuBarSortArray) {
        const menuBarSortArray = JSON.parse(
          JSON.stringify(stateSettings.menuBarSortArray)
        );
        if (payload.copiedDatabase) {
          let inserted = false;
          menuBarSortArray.forEach((item, index) => {
            updatedMenuBarSortArray.push({
              name: item.name,
              position: updatedMenuBarSortArray.length,
            });
            if (
              item.name === payload.copiedDatabase.databaseName &&
              !inserted
            ) {
              updatedMenuBarSortArray.push({
                name: payload.copiedDatabase.copiedDatabaseName,
                position: updatedMenuBarSortArray.length,
              });
              inserted = true;
            }
          });
        } else if (payload.deletedDatabase) {
          updatedMenuBarSortArray = menuBarSortArray.filter(
            (item) => item.name !== payload.deletedDatabase
          );
        }
      }
      if (
        stateSettings &&
        stateSettings.hasOwnProperty('customDatabaseSchema')
      ) {
        let customDatabaseSchema = JSON.parse(
          JSON.stringify(stateSettings.customDatabaseSchema)
        );

        if (customDatabaseSchema) {
          const settingSchemaDatabaseArray = Object.keys(customDatabaseSchema);
          let shouldUpdateSetting = false;

          if (
            settingSchemaDatabaseArray &&
            settingSchemaDatabaseArray.length > 0
          ) {
            const dataList =
              payload && payload.databaselist
                ? payload.databaselist
                : state.databaseList;
            const DbNameArray = JSON.parse(JSON.stringify(dataList)).map(
              (db) => db.name
            );
            settingSchemaDatabaseArray.map((db) => {
              if (!DbNameArray.includes(db)) {
                shouldUpdateSetting = true;
                delete customDatabaseSchema[db];
              }
            });
          }

          if (shouldUpdateSetting) {
            const updatedUserSettings = {
              ...stateSettings,
              customDatabaseSchema: customDatabaseSchema,
              ...(updatedMenuBarSortArray.length && {
                menuBarSortArray: updatedMenuBarSortArray,
              }),
            };
            isUpdatedSettings = true;
            await dispatch('updateUserSettings', updatedUserSettings);
          }
        }
      }
      if (!isUpdatedSettings && updatedMenuBarSortArray.length) {
        const updatedUserSettings = {
          ...stateSettings,
          menuBarSortArray: updatedMenuBarSortArray,
        };
        await dispatch('updateUserSettings', updatedUserSettings);
      }
    },
    async fetchUserDataBase({ commit, state, dispatch }, copiedDatabase) {
      try {
        const fetchUserDatabasesResponse = await getUserDatabases();

        if (
          fetchUserDatabasesResponse &&
          fetchUserDatabasesResponse.status === 200 &&
          fetchUserDatabasesResponse.data
        ) {
          const finalData = [...fetchUserDatabasesResponse.data].map((el) => {
            const database = state.databaseList.find(
              (ele) => ele.name.trim() === el.name.trim()
            );
            if (database && database.schema) {
              return { ...el, schema: database.schema };
            }

            return el;
          });
          commit('SET_DATABASE_LIST', finalData);

          //Add pagination Dbs: For these databases, we are calling the V8 data API when fetching items and duplicating items
          const paginationDbs = [];
          for (const database of finalData) {
            if (database.pagination) {
              paginationDbs.push(database.name);
            }
          }
          await dispatch('addPaginationDB', paginationDbs);
          await dispatch('filterSettingBasedOnDatabases', {
            databaselist: finalData,
            copiedDatabase,
          });
        }
      } catch (error) {
        console.log(error, 'errorrrrrrr');
        // eslint-disable-next-line no-console
        console.log('error :>> ', error);
      }
    },
    setDatabaseSchema({ commit }, data) {
      commit('SET_DATABASE_SCHEMA', data);
    },
    async onLogOut({ commit }) {
      localStorage.clear();
      commit('setUser', null);
      await Auth.signOut();
      window.location.href = window.location.origin + '/#' + '/auth';
    },
    async refreshSession({ commit, getters }) {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const currentSession = await Auth.currentSession();
        cognitoUser.refreshSession(
          currentSession.refreshToken,
          (err, session) => {
            // Replace user with new token
            let user = getters.getUser;
            user.signInUserSession = session;

            // Set updated user
            commit('setUser', user);

            return session;
          }
        );
      } catch (e) {
        // Verification failed, log out
        await dispatch('onLogOut');
        return null;
      }
    },
    async checkTokenRefresh({ getters, dispatch }) {
      console.log('==> Store Token refresh check <=='); // used for testing the update event

      if (
        getters.getAccessTokenExpiry &&
        getters.getAccessTokenExpiry !== null
      ) {
        const currentTime = Date.now();
        const tokenExpiryTime = getters.getAccessTokenExpiry * 1000; //in milliseconds

        if (tokenExpiryTime < currentTime) {
          await dispatch('onLogOut');
        } else {
          const updateTime = 30 * 60000; //in milliseconds
          const shouldRefreshToken = tokenExpiryTime - currentTime < updateTime;
          if (shouldRefreshToken) {
            dispatch('refreshSession');
          }
        }
      }
    },
    setWorkFlowPerTag({ commit }, data) {
      commit('SET_WORKFLOWS_PER_TAG', data);
    },
    setTagPerWorkFlow({ commit }, data) {
      commit('SET_TAGS_PER_WORKFLOW', data);
    },
    async fetchWorkFlowTags({ dispatch }) {
      try {
        const res = await getWorkFlowTags();
        if (res?.status == 200 && res?.data?.length > 0) {
          dispatch('setWorkFlowPerTag', res.data);
          const tagsPerWorkFlow = res.data.reduce((acc, data) => {
            data.resources.map((el) => {
              if (!acc[el]) {
                acc[el] = [data];
              } else {
                acc[el].push(data);
              }
            });
            return acc;
          }, {});
          dispatch('setTagPerWorkFlow', tagsPerWorkFlow);
        }
      } catch (e) {
        console.log(e);
      }
    },
    addPaginationDB({ commit }, data) {
      commit('ADD_PAGINATION_DBS', data);
    },
    addSidebarLinkedCustomDBSettings({ commit }, data) {
      commit('setSidebarLinkedCustomDBSettings', data);
    },
  },
});

export default store;
